/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    Component,
    Injector,
    Input,
    Output,
    EventEmitter,
    Inject,
    Optional,
    NgZone,
    OnInit,
    forwardRef
} from '@angular/core';
import { MatBottomSheet, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { TranslateModule } from '@ngx-translate/core';
import { QRCodeComponent } from 'angularx-qrcode';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { BackToCloseModalDirective } from '../../directives/back-to-close-modal.directive';
import { NvFocusRippleDirective } from '../../directives/focus-ripple.directive';
import { HyperLinkUserActivationGuardDirective } from '../../directives/hyperlink-user-activation-guard.directive';
import { OffDeviceLinkDirective } from '../../directives/off-device-link.directive';
import { SpatialNavigationContainerDirective } from '../../directives/spatial-navigation-container.directive';
import { TrapFocusDirective } from '../../directives/trap-focus.directive';
import { NvHigConfig, NV_HIG_CONFIG } from '../../hig.config';
import {
    NvGenericDialogConfig,
    NvGenericDialogData,
    NvHigBottomSheetRef,
    addPanelClass
} from '../nv-generic-dialog/nv-generic-dialog.types';

@Component({
    selector: 'nv-hig-bottom-sheet',
    templateUrl: './nv-hig-bottom-sheet.component.html',
    styleUrls: ['./nv-hig-bottom-sheet.component.scss'],
    imports: [
        FlexModule,
        SpatialNavigationContainerDirective,
        TrapFocusDirective,
        QRCodeComponent,
        NgClass,
        ExtendedModule,
        MatProgressSpinnerModule,
        forwardRef(() => OffDeviceLinkDirective),
        HyperLinkUserActivationGuardDirective,
        NgTemplateOutlet,
        MatButtonModule,
        NvFocusRippleDirective,
        AutoFocusDirective,
        TranslateModule
    ]
})
export class NvHigBottomSheetComponent extends BackToCloseModalDirective implements OnInit {
    @Output() primaryButtonClick = new EventEmitter<MouseEvent>();
    @Output() secondaryButtonClick = new EventEmitter<MouseEvent>();
    @Output() linkClick = new EventEmitter();
    @Input() data: NvGenericDialogData = {};

    private showQRCodeForLinks: boolean;
    tvMode: boolean;
    layoutType: string;

    get hasImage(): boolean {
        return !!this.data.icon || !!this.data.image || this.showQRCode;
    }

    get showQRCode(): boolean {
        return this.showQRCodeForLinks && !!this.data.link?.url;
    }

    get autoFocusOnPrimaryButton(): boolean {
        return this.data.autoFocusOnPrimaryButton ?? true;
    }

    get showAccentBtn(): boolean {
        return this.data.primaryButton.accentColor && !this.tvMode;
    }

    static open(matBottomSheet: MatBottomSheet, config: NvGenericDialogConfig, tvMode: boolean): NvHigBottomSheetRef {
        addPanelClass(config, { bottomSheet: true, tvMode });
        return matBottomSheet.open(NvHigBottomSheetComponent, config);
    }

    constructor(
        injector: Injector,
        @Optional() @Inject(NV_HIG_CONFIG) config: NvHigConfig,
        @Optional() dialogRef: MatDialogRef<any>,
        @Optional() bottomSheetRef: MatBottomSheetRef<any>,
        ngZone: NgZone
    ) {
        super(dialogRef, bottomSheetRef, injector, ngZone);
        this.data = injector.get(MAT_BOTTOM_SHEET_DATA, null);
        this.tvMode = !!config.tvMode;
        this.showQRCodeForLinks = !!config.showQRCodeForLinks;
    }

    ngOnInit() {
        this.layoutType = this.hasImage || this.tvMode ? 'row' : 'column';
    }

    onPrimaryButtonClick(event: MouseEvent) {
        this.primaryButtonClick.emit(event);
    }

    onSecondaryButtonClick(event: MouseEvent) {
        this.secondaryButtonClick.emit(event);
    }

    onLinkClick() {
        this.linkClick.emit();
    }
}
